"use client";
/**
 * SharedPieceworkItemEntry.jsx — FULL REPLACEMENT
 *
 * Changes vs previous version:
 *
 *   LF inputs in INCHES:
 *     - entry.lengthIn stores raw inches (free-text string).
 *     - parseInches() accepts: "119"  "119.5"  "119-1/2"  "9'11\""  "10'"  "10' 1-1/4\""
 *     - calcLaborQtyInst divides lengthIn/12 to get feet before multiplying by qty.
 *     - LFDimInput label = "Length (inches)", preview shows "→ X" (Y ft)".
 *     - buildLineItems in CreatePieceworkRequest sends length = inches/12 for backend.
 *
 *   SF inputs — decimal-friendly:
 *     - parseFtPair handles "4.5x8"  "4x10"  "4.5 x 10" etc. correctly.
 *     - Combined field placeholder shows decimal examples.
 *     - Split fields accept plain decimals: "4.5", "9.5" treated as feet.
 *
 *   Duplicate & delete per row instance:
 *     - entries replaced by instances[] array:
 *       { _instId, pairedIdx, qty, lengthIn, height, length, combinedDim }
 *     - Each paired item can have multiple instances (for different sizes).
 *     - Dup button always shown; Del shown only when >1 instance for that item.
 *     - Parent manages instances via onInstChange/onDuplicate/onRemove props.
 *
 *   Duplicate size validation (exported):
 *     - validateEntries(instances, module) → string[]
 *     - Returns errors when two instances of the same pairedIdx share an identical
 *       effective size key (combined dim string or H×L or LF inches).
 *
 *   Work completed date:
 *     - Shown at top of items step (workDate / onWorkDateChange props).
 *     - Defaults to today, max = today, "Reset to today" link if changed.
 *
 *   Backward compat:
 *     - read-only paths still accept entries={} keyed by pairedIdx.
 *     - savedCoverages / savedPieceCounts unchanged.
 *     - manualItems / ManualLaborRow unchanged for EditableRequestCard.
 */

import { useMemo, useState } from "react";

// ─────────────────────────────────────────────────────────────────────────────
// Utility
// ─────────────────────────────────────────────────────────────────────────────
const fmt = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const fmtQty = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
// Rates need finer precision than dollar totals — piecework rates are
// often fractional (e.g. $0.5938/SF) and 2dp rounding can silently
// misstate what's actually being paid per unit.
const fmtRate = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  });
const cls = (...c) => c.filter(Boolean).join(" ");

export const isSF = (uom) => (uom || "").toUpperCase().includes("SF");
export const isLF = (uom) => (uom || "").toUpperCase().includes("LF");
export const requiresDims = (uom) => isSF(uom) || isLF(uom);

// ─────────────────────────────────────────────────────────────────────────────
// Parsers
// ─────────────────────────────────────────────────────────────────────────────
const parseFraction = (s) => {
  const m = String(s)
    .trim()
    .match(/^(\d+)\/(\d+)$/);
  return m ? parseInt(m[1], 10) / parseInt(m[2], 10) : null;
};

/** parseInches(s) → decimal inches | null — for LF length fields */
export const parseInches = (s) => {
  if (s == null) return null;
  s = String(s).trim();
  if (!s) return null;
  // feet + inch whole+fraction: "9' 11-1/4\""
  let m = s.match(/^(\d+(?:\.\d+)?)\s*'\s*(\d+)-(\d+\/\d+)"?$/);
  if (m)
    return (
      parseFloat(m[1]) * 12 + parseInt(m[2], 10) + (parseFraction(m[3]) ?? 0)
    );
  // feet + whole inches: "9' 11\""
  m = s.match(/^(\d+(?:\.\d+)?)\s*'\s*(\d+(?:\.\d+?)?)"?$/);
  if (m) return parseFloat(m[1]) * 12 + parseFloat(m[2]);
  // bare feet: "10'"
  m = s.match(/^(\d+(?:\.\d+)?)\s*'$/);
  if (m) return parseFloat(m[1]) * 12;
  // whole+fraction inches: "119-1/2"
  m = s.match(/^(\d+)-(\d+\/\d+)"?$/);
  if (m) return parseInt(m[1], 10) + (parseFraction(m[2]) ?? 0);
  // plain decimal/whole inches: "119.5" "119\""
  m = s.match(/^(\d+(?:\.\d+?)?)"?$/);
  if (m) return parseFloat(m[1]);
  return null;
};

/** parseFt(s) → decimal feet | null — for SF dimension fields */
export const parseFt = (s) => {
  if (s == null) return null;
  s = String(s).trim();
  if (!s) return null;
  let m = s.match(/^(\d+(?:\.\d+)?)\s*'\s*(\d+)-(\d+\/\d+)"?$/);
  if (m)
    return (
      parseFloat(m[1]) + (parseInt(m[2], 10) + (parseFraction(m[3]) ?? 0)) / 12
    );
  m = s.match(/^(\d+(?:\.\d+)?)\s*'\s*(\d+(?:\.\d+?)?)"?$/);
  if (m) return parseFloat(m[1]) + parseFloat(m[2]) / 12;
  m = s.match(/^(\d+(?:\.\d+)?)\s*'$/);
  if (m) return parseFloat(m[1]);
  m = s.match(/^(\d+)-(\d+\/\d+)"?$/);
  if (m) return (parseInt(m[1], 10) + (parseFraction(m[2]) ?? 0)) / 12;
  m = s.match(/^(\d+(?:\.\d+?)?)"$/);
  if (m) return parseFloat(m[1]) / 12;
  m = s.match(/^(\d+(?:\.\d+)?)$/);
  if (m) return parseFloat(m[1]);
  return null;
};

/** parseFtPair(s) → [ftA, ftB] | null — "4x8" "4.5x8" "4x10" "4.5 x 10" */
export const parseFtPair = (s) => {
  if (!s) return null;
  s = String(s).trim();
  const parts = s.split(/\s*[x×*]\s*|\s+/).filter(Boolean);
  if (parts.length === 2) {
    const a = parseFt(parts[0]);
    const b = parseFt(parts[1]);
    if (a !== null && b !== null) return [a, b];
  }
  return null;
};

const resolveEntryFt = (val) => {
  if (val == null || val === "") return 0;
  const n = parseFt(String(val));
  return n !== null ? n : parseFloat(val) || 0;
};

// ─────────────────────────────────────────────────────────────────────────────
// Size key for duplicate detection
// ─────────────────────────────────────────────────────────────────────────────
const sizeKey = (inst, laborUom) => {
  if (isSF(laborUom)) {
    const cd = (inst.combinedDim || "").trim();
    if (cd) return cd.toLowerCase().replace(/\s+/g, "");
    return `${resolveEntryFt(inst.height).toFixed(4)}x${resolveEntryFt(inst.length).toFixed(4)}`;
  }
  if (isLF(laborUom)) {
    const inches = parseInches(String(inst.lengthIn ?? ""));
    return inches !== null
      ? inches.toFixed(4)
      : String(inst.lengthIn ?? "").trim();
  }
  return "";
};

// ─────────────────────────────────────────────────────────────────────────────
// calcLaborQtyInst — coverage from one instance
// ─────────────────────────────────────────────────────────────────────────────
export function calcLaborQtyInst(paired, inst) {
  const laborUom = paired.labor?.uom || "";
  const sf = isSF(laborUom);
  const lf = isLF(laborUom);
  const qty = parseFloat(inst?.qty) || 0;

  if (paired.material) {
    // Material+labor: qty = piece count, dims = per-piece size
    if (sf) {
      const h = resolveEntryFt(inst?.height);
      const l = resolveEntryFt(inst?.length);
      return parseFloat((qty * h * l).toFixed(2));
    }
    if (lf) {
      const inches = parseInches(String(inst?.lengthIn ?? "")) ?? 0;
      return parseFloat((qty * (inches / 12)).toFixed(2));
    }
    return qty;
  }

  // Labor-only: qty = piece count, dims = per-piece size
  // Coverage = qty × per-piece coverage
  if (sf) {
    const h = resolveEntryFt(inst?.height);
    const l = resolveEntryFt(inst?.length);
    return parseFloat((qty * h * l).toFixed(2));
  }
  if (lf) {
    const inches = parseInches(String(inst?.lengthIn ?? "")) ?? 0;
    return parseFloat((qty * (inches / 12)).toFixed(2));
  }
  return qty;
}

// Backward-compat alias (used by read-only paths)
export function calcLaborQty(paired, entry) {
  return calcLaborQtyInst(paired, entry);
}

export function calcManualQty(item) {
  if (isSF(item.uom))
    return parseFloat(((item.height || 0) * (item.length || 0)).toFixed(2));
  if (isLF(item.uom)) return parseFloat((item.length || 0).toFixed(2));
  return item.quantity || 0;
}

// ─────────────────────────────────────────────────────────────────────────────
// validateEntries — returns error strings for duplicate sizes
// ─────────────────────────────────────────────────────────────────────────────
export function validateEntries(instances, module) {
  if (!module) return [];
  const errors = [];
  const byPaired = new Map();
  for (const inst of instances) {
    if (!byPaired.has(inst.pairedIdx)) byPaired.set(inst.pairedIdx, []);
    byPaired.get(inst.pairedIdx).push(inst);
  }
  for (const [pIdx, insts] of byPaired.entries()) {
    if (insts.length < 2) continue;
    const paired = module.pairedItems?.[pIdx];
    if (!paired) continue;
    const uom = paired.labor?.uom || "";
    if (!isSF(uom) && !isLF(uom)) continue;
    const seen = new Set();
    for (const inst of insts) {
      const k = sizeKey(inst, uom);
      if (k && seen.has(k)) {
        const name =
          paired.material?.name ||
          paired.labor?.description ||
          `Item ${pIdx + 1}`;
        errors.push(
          `"${name}" has duplicate sizes — adjust the size or remove the duplicate row.`,
        );
        break;
      }
      seen.add(k);
    }
  }
  return errors;
}

// ─────────────────────────────────────────────────────────────────────────────
// makeInst — factory for new instance objects
// ─────────────────────────────────────────────────────────────────────────────
let _instCounter = 0;
export const makeInst = (pairedIdx, overrides = {}) => ({
  _instId: `inst_${++_instCounter}_${Date.now()}`,
  pairedIdx,
  qty: "",
  lengthIn: "",
  height: "",
  length: "",
  combinedDim: "",
  ...overrides,
});

// ─────────────────────────────────────────────────────────────────────────────
// NumInput
// ─────────────────────────────────────────────────────────────────────────────
function NumInput({ value, onChange, placeholder, step = "0.25", disabled }) {
  return (
    <input
      type="number"
      min="0"
      step={step}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      className="w-full px-2 py-1.5 text-xs bg-gray-700 border border-gray-600 rounded-lg text-gray-200 placeholder-gray-600 focus:border-orange-500 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none disabled:opacity-50"
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SFDimRow — combined "4x8" / "4.5x8" / "4x10" or split H / W in feet
// ─────────────────────────────────────────────────────────────────────────────
function SFDimRow({ inst, onInstChange, defaultHeightFt, defaultLengthFt }) {
  const [combined, setCombined] = useState(true);

  const combinedRaw = inst.combinedDim ?? "";
  const pairResult = parseFtPair(combinedRaw);
  const combinedOk = !!pairResult;

  const resolvedH = combined
    ? (pairResult?.[0] ?? 0)
    : resolveEntryFt(inst.height);
  const resolvedL = combined
    ? (pairResult?.[1] ?? 0)
    : resolveEntryFt(inst.length);

  const ph =
    defaultHeightFt && defaultLengthFt
      ? `e.g. ${defaultHeightFt}x${defaultLengthFt}  or  4.5x8  or  4x10`
      : "e.g. 4x8  4.5x8  4x10  9'x4'";

  const handleCombined = (val) => {
    const pair = parseFtPair(val);
    onInstChange("combinedDim", val);
    if (pair) {
      onInstChange("height", String(pair[0]));
      onInstChange("length", String(pair[1]));
    } else {
      const ft = parseFt(val);
      if (ft !== null) {
        onInstChange("height", String(defaultHeightFt || ""));
        onInstChange("length", String(ft));
      }
    }
  };

  const handleSplit = (field, val) => {
    onInstChange(field, val);
    onInstChange("combinedDim", "");
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <label className="text-[9px] text-gray-500 uppercase tracking-wide">
          Size per piece{" "}
          <span className="text-gray-600 normal-case font-normal">(ft)</span>
        </label>
        <button
          type="button"
          onClick={() => setCombined((c) => !c)}
          className="text-[9px] text-gray-600 hover:text-gray-400 transition-colors"
        >
          {combined ? "split H / W" : "combined (e.g. 4×8)"}
        </button>
      </div>

      {combined ? (
        <div>
          <input
            type="text"
            inputMode="decimal"
            value={combinedRaw}
            onChange={(e) => handleCombined(e.target.value)}
            placeholder={ph}
            className={cls(
              "w-full px-2 py-1.5 text-xs bg-gray-700 border rounded-lg font-mono placeholder-gray-600 focus:outline-none",
              combinedRaw === ""
                ? "border-gray-600 text-gray-200 focus:border-orange-500"
                : combinedOk
                  ? "border-emerald-600/60 text-emerald-300 focus:border-emerald-500"
                  : "border-amber-600/60 text-amber-300 focus:border-amber-500",
            )}
          />
          {combinedRaw !== "" && (
            <p
              className={cls(
                "text-[9px] font-mono mt-0.5",
                combinedOk ? "text-emerald-500" : "text-amber-600",
              )}
            >
              {combinedOk
                ? `→ ${pairResult[0].toFixed(3)} ft × ${pairResult[1].toFixed(3)} ft = ${(pairResult[0] * pairResult[1]).toFixed(2)} SF / pc`
                : "⚠ try 4x8  4.5x8  4x10  9'x4'"}
            </p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          {[
            [
              "height",
              "Height (ft)",
              defaultHeightFt ? String(defaultHeightFt) : "e.g. 9",
            ],
            [
              "length",
              "Width (ft)",
              defaultLengthFt ? String(defaultLengthFt) : "e.g. 4",
            ],
          ].map(([field, lbl, ph2]) => {
            const raw = inst[field] ?? "";
            const parsed = resolveEntryFt(raw);
            const ok = raw !== "" && parsed > 0;
            const bad = raw !== "" && parsed === 0;
            return (
              <div key={field}>
                <label className="text-[9px] text-gray-500 uppercase tracking-wide block mb-1">
                  {lbl}
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={raw}
                  onChange={(e) => handleSplit(field, e.target.value)}
                  placeholder={ph2}
                  className={cls(
                    "w-full px-2 py-1.5 text-xs bg-gray-700 border rounded-lg font-mono placeholder-gray-600 focus:outline-none",
                    ok
                      ? "border-emerald-600/60 text-emerald-300 focus:border-emerald-500"
                      : bad
                        ? "border-amber-600/60 text-amber-300 focus:border-amber-500"
                        : "border-gray-600 text-gray-200 focus:border-orange-500",
                  )}
                />
                {raw !== "" && (
                  <p
                    className={cls(
                      "text-[9px] font-mono mt-0.5",
                      ok ? "text-emerald-500" : "text-amber-600",
                    )}
                  >
                    {ok
                      ? `→ ${parsed.toFixed(3)} ft`
                      : "⚠ e.g. 9  or  9.5  or  9'"}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {resolvedH > 0 && resolvedL > 0 && (
        <div className="flex items-center gap-2 px-2 py-1 bg-gray-900/60 border border-gray-700 rounded-lg">
          <span className="text-[9px] text-gray-500">Per piece:</span>
          <span className="text-[10px] font-mono text-emerald-400">
            {resolvedH.toFixed(3)} × {resolvedL.toFixed(3)} ={" "}
            {(resolvedH * resolvedL).toFixed(2)} SF
          </span>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LFDimInput — input and display in INCHES
// ─────────────────────────────────────────────────────────────────────────────
function LFDimInput({
  inst,
  onInstChange,
  defaultLengthInches,
  defaultLengthFt,
}) {
  const raw = inst.lengthIn ?? "";
  const parsed = raw !== "" ? parseInches(String(raw)) : null;
  const hasVal = raw !== "";
  const ok = hasVal && parsed !== null && parsed > 0;
  const bad = hasVal && (parsed === null || parsed === 0);

  const ph = defaultLengthInches
    ? `Default: ${defaultLengthInches}" — e.g. 119-1/2 or 9'11"`
    : defaultLengthFt
      ? `Default: ${(defaultLengthFt * 12).toFixed(1)}" — e.g. 119 or 9'11"`
      : "e.g. 119  119-1/2  9'11\"  10'";

  return (
    <div className="space-y-0.5">
      <label className="text-[9px] text-gray-500 uppercase tracking-wide block mb-1">
        Length per piece{" "}
        <span className="text-gray-600 normal-case font-normal">(inches)</span>
      </label>
      <input
        type="text"
        inputMode="decimal"
        value={raw}
        onChange={(e) => onInstChange("lengthIn", e.target.value)}
        placeholder={ph}
        className={cls(
          "w-full px-2 py-1.5 text-xs bg-gray-700 border rounded-lg font-mono placeholder-gray-600 focus:outline-none",
          ok
            ? "border-emerald-600/60 text-emerald-300 focus:border-emerald-500"
            : bad
              ? "border-amber-600/60 text-amber-300 focus:border-amber-500"
              : "border-gray-600 text-gray-200 focus:border-orange-500",
        )}
      />
      {hasVal && (
        <p
          className={cls(
            "text-[9px] font-mono",
            ok ? "text-emerald-500" : "text-amber-600",
          )}
        >
          {ok
            ? `→ ${parsed.toFixed(3)}" = ${(parsed / 12).toFixed(4)} ft`
            : "⚠ try 119  119-1/2  119.5  9'11\""}
        </p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ModulePairedRow — one paired item with all its size instances
// ─────────────────────────────────────────────────────────────────────────────
function ModulePairedRow({
  paired,
  pairedIdx,
  instances,
  onInstChange,
  onDuplicate,
  onRemove,
  workerBurdenMultiplier,
  hstRate,
  readOnly,
  savedCoverage,
  savedPieceCount,
  sizeErrors,
}) {
  const laborUom = paired.labor?.uom || "";
  const sf = isSF(laborUom);
  const lf = isLF(laborUom);
  const hasMaterial = !!paired.material;
  const unitRate = paired.labor?.unitRate || 0;
  const workerRate = unitRate;

  const defHeightFt = paired.material?.defaultHeightFt || 0;
  const defLengthFt = paired.material?.defaultLengthFt || 0;
  const defLengthIn = paired.material?.defaultLengthInches || 0;

  const totalQty =
    readOnly && savedCoverage != null
      ? savedCoverage
      : instances.reduce((s, inst) => s + calcLaborQtyInst(paired, inst), 0);
  const totalEarnings = parseFloat((totalQty * workerRate).toFixed(2));
  const totalHst = parseFloat(
    ((totalEarnings * (hstRate || 0)) / 100).toFixed(2),
  );
  const hasSuggested =
    !hasMaterial && (paired.suggestedQty || 0) > 0 && !readOnly;

  // Summary bar — shared between edit and read-only
  const SummaryBar = ({ qty }) => {
    const sub = parseFloat((qty * workerRate).toFixed(2));
    if (qty <= 0) return null;
    return (
      <div className="grid grid-cols-3 divide-x divide-gray-700/50 bg-gray-900/60 rounded-lg border border-gray-700 overflow-hidden">
        {[
          {
            label: "Qty",
            value: (
              <span className="text-blue-300 font-bold font-mono text-xs">
                {fmtQty(qty)}{" "}
                <span className="text-gray-500 text-[9px] font-normal">
                  {laborUom}
                </span>
              </span>
            ),
          },
          {
            label: "Rate",
            value: (
              <span className="text-gray-300 font-mono text-xs">
                ${fmtRate(workerRate)}
              </span>
            ),
          },
          {
            label: "Subtotal",
            value: (
              <span className="text-orange-300 font-bold font-mono text-xs">
                ${fmt(sub)}
              </span>
            ),
          },
        ].map(({ label, value }) => (
          <div key={label} className="px-2 py-2 flex flex-col gap-0.5">
            <span className="text-[8px] text-gray-500 uppercase tracking-wide">
              {label}
            </span>
            {value}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div
      className={cls(
        "rounded-xl border overflow-hidden transition-colors",
        totalQty > 0 ? "border-orange-500/40" : "border-gray-700",
      )}
    >
      {/* Header */}
      <div
        className={cls(
          "flex items-center gap-2 px-3 py-2 border-b border-gray-700/60",
          hasMaterial ? "bg-amber-950/20" : "bg-blue-950/20",
        )}
      >
        <span className="text-[10px] font-mono text-gray-600 w-5 text-center shrink-0">
          {pairedIdx + 1}
        </span>
        {hasMaterial ? (
          <>
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-900/50 text-amber-300 border border-amber-700/40 uppercase tracking-wide shrink-0">
              Mat+Lab
            </span>
            <span className="text-[10px] text-gray-300 truncate flex-1 font-medium">
              {paired.material.name || "—"}
            </span>
          </>
        ) : (
          <>
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-blue-900/50 text-blue-300 border border-blue-700/40 uppercase tracking-wide shrink-0">
              Labor
            </span>
            <span className="text-[10px] font-mono text-blue-300 shrink-0">
              {paired.labor?.itemCode || "—"}
            </span>
            <span className="text-[10px] text-gray-400 truncate flex-1">
              {paired.labor?.description}
            </span>
          </>
        )}
        {(sf || lf) && (
          <span className="text-[9px] bg-gray-700 text-gray-400 px-1.5 py-0.5 rounded border border-gray-600 shrink-0">
            {sf ? "SF" : "LF"}
          </span>
        )}
      </div>

      <div className="bg-gray-800 px-3 pb-3 space-y-2.5">
        {/* Labor info + rate */}
        <div className="flex items-center justify-between gap-3 pt-3">
          <div className="min-w-0">
            <p className="text-[10px] text-gray-500 truncate">
              {paired.labor?.description || "—"}
            </p>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-[10px] font-mono text-orange-400">
                {paired.labor?.itemCode}
              </span>
              <span className="text-[10px] text-gray-600">·</span>
              <span className="text-[10px] text-gray-500">{laborUom}</span>
            </div>
          </div>
          <div className="text-right shrink-0">
            <p className="text-[10px] text-gray-500">Unit rate</p>
            <p className="text-sm font-mono font-bold text-green-400">
              ${fmtRate(workerRate)}/{laborUom}
            </p>
          </div>
        </div>

        {/* Suggested qty */}
        {hasSuggested && (
          <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg border bg-purple-950/30 border-purple-800/40 text-[11px] text-purple-300">
            <span className="text-purple-500 shrink-0">~</span>
            <span className="font-medium">
              Suggested: {fmtQty(paired.suggestedQty)} {laborUom}
            </span>
            {instances.length === 1 && !instances[0].qty && (
              <button
                onClick={() =>
                  onInstChange(
                    instances[0]._instId,
                    "qty",
                    String(paired.suggestedQty),
                  )
                }
                className="ml-auto text-[10px] text-purple-400 hover:text-purple-200 border border-purple-700/50 px-1.5 py-0.5 rounded transition-colors"
              >
                Use suggested
              </button>
            )}
          </div>
        )}

        {/* ── READ-ONLY ── */}
        {readOnly && (
          <div className="space-y-2 pt-1">
            {hasMaterial && savedPieceCount != null && savedPieceCount > 0 && (
              <p className="text-xs text-gray-400">
                {fmtQty(savedPieceCount)} pcs
              </p>
            )}
            <SummaryBar qty={totalQty} />
          </div>
        )}

        {/* ── EDIT MODE — instance rows ── */}
        {!readOnly &&
          instances.map((inst, i) => {
            const instQty = calcLaborQtyInst(paired, inst);
            const hasSizeErr = sizeErrors?.has(inst._instId);
            const canDelete = instances.length > 1;

            return (
              <div
                key={inst._instId}
                className={cls(
                  "rounded-lg border p-2.5 space-y-2",
                  hasSizeErr
                    ? "border-rose-700/60 bg-rose-950/20"
                    : instQty > 0
                      ? "border-orange-600/30 bg-gray-900/40"
                      : "border-gray-700/60 bg-gray-900/20",
                )}
              >
                {/* Instance toolbar */}
                <div className="flex items-center justify-between">
                  {instances.length > 1 && (
                    <span className="text-[9px] text-gray-600 font-mono">
                      Size #{i + 1}
                    </span>
                  )}
                  <div className="flex items-center gap-1 ml-auto">
                    <button
                      onClick={() => onDuplicate(inst._instId)}
                      title="Duplicate this size"
                      className="flex items-center gap-1 px-1.5 py-0.5 text-[9px] text-gray-500 hover:text-sky-400 border border-gray-700 hover:border-sky-700/50 rounded transition-colors"
                    >
                      <svg
                        className="w-3 h-3"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                        <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                      </svg>
                      Dup
                    </button>
                    {canDelete && (
                      <button
                        onClick={() => onRemove(inst._instId)}
                        title="Remove this size"
                        className="flex items-center gap-1 px-1.5 py-0.5 text-[9px] text-gray-500 hover:text-rose-400 border border-gray-700 hover:border-rose-700/50 rounded transition-colors"
                      >
                        <svg
                          className="w-3 h-3"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Del
                      </button>
                    )}
                  </div>
                </div>

                {hasSizeErr && (
                  <p className="text-[9px] text-rose-400">
                    ⚠ Same size as another row — adjust the dimensions or remove
                    this row.
                  </p>
                )}

                {/* Material inputs */}
                {hasMaterial && (
                  <div className="space-y-2">
                    <div>
                      <label className="text-[9px] text-gray-500 uppercase tracking-wide block mb-1">
                        Qty (pieces)
                      </label>
                      <NumInput
                        value={inst.qty}
                        onChange={(v) => onInstChange(inst._instId, "qty", v)}
                        placeholder="0"
                        step="1"
                      />
                    </div>
                    {sf && (
                      <SFDimRow
                        inst={inst}
                        onInstChange={(f, v) =>
                          onInstChange(inst._instId, f, v)
                        }
                        defaultHeightFt={defHeightFt || undefined}
                        defaultLengthFt={defLengthFt || undefined}
                      />
                    )}
                    {lf && (
                      <LFDimInput
                        inst={inst}
                        onInstChange={(f, v) =>
                          onInstChange(inst._instId, f, v)
                        }
                        defaultLengthInches={defLengthIn || undefined}
                        defaultLengthFt={defLengthFt || undefined}
                      />
                    )}
                    <p className="text-[9px] text-amber-700/70">
                      Material qty used to compute coverage only — not invoiced
                    </p>
                  </div>
                )}

                {/* Labor-only inputs */}
                {!hasMaterial && (
                  <div className="space-y-2">
                    {/* QTY always shown first for LF/SF — qty = number of pieces */}
                    {(sf || lf) && (
                      <div>
                        <label className="text-[9px] text-gray-500 uppercase tracking-wide block mb-1">
                          Qty{" "}
                          <span className="text-gray-600 normal-case font-normal">
                            {lf
                              ? "(# of pieces at this length)"
                              : "(# of panels at these dimensions)"}
                          </span>
                        </label>
                        <NumInput
                          value={inst.qty}
                          onChange={(v) => onInstChange(inst._instId, "qty", v)}
                          placeholder={
                            paired.labor?.defaultQty != null &&
                            paired.labor?.defaultQty !== ""
                              ? String(paired.labor.defaultQty)
                              : "0"
                          }
                          step="1"
                        />
                      </div>
                    )}

                    {/* Dimension input — per-piece size */}
                    {sf ? (
                      <SFDimRow
                        inst={inst}
                        onInstChange={(f, v) =>
                          onInstChange(inst._instId, f, v)
                        }
                        defaultHeightFt={
                          paired.labor?.defaultHeightFt || undefined
                        }
                        defaultLengthFt={
                          paired.labor?.defaultLengthFt || undefined
                        }
                      />
                    ) : lf ? (
                      <LFDimInput
                        inst={inst}
                        onInstChange={(f, v) =>
                          onInstChange(inst._instId, f, v)
                        }
                        defaultLengthInches={
                          paired.labor?.defaultLengthInches || undefined
                        }
                        defaultLengthFt={
                          paired.labor?.defaultLengthFt || undefined
                        }
                      />
                    ) : (
                      /* EA — just qty, no dims */
                      <div>
                        <label className="text-[9px] text-gray-500 uppercase tracking-wide block mb-1">
                          Count
                        </label>
                        <NumInput
                          value={inst.qty}
                          onChange={(v) => onInstChange(inst._instId, "qty", v)}
                          placeholder={
                            paired.labor?.defaultQty != null &&
                            paired.labor?.defaultQty !== ""
                              ? String(paired.labor.defaultQty)
                              : hasSuggested
                                ? String(paired.suggestedQty)
                                : "0"
                          }
                          step="1"
                        />
                      </div>
                    )}

                    {/* Coverage preview — shown once qty AND dims are both set */}
                    {(sf || lf) &&
                      (() => {
                        const qty = parseFloat(inst.qty) || 0;
                        if (qty <= 0) return null;
                        if (lf) {
                          const inches = parseInches(
                            String(inst.lengthIn ?? ""),
                          );
                          if (!inches) return null;
                          const totalLF = parseFloat(
                            ((qty * inches) / 12).toFixed(2),
                          );
                          return (
                            <div className="flex items-center gap-2 px-2 py-1.5 bg-blue-950/30 border border-blue-800/40 rounded-lg">
                              <span className="text-[9px] text-blue-400 uppercase tracking-wide">
                                Coverage
                              </span>
                              <span className="text-[10px] font-mono text-blue-300">
                                {qty} × {inches.toFixed(3)}" = {totalLF} LF
                              </span>
                            </div>
                          );
                        }
                        if (sf) {
                          const h = resolveEntryFt(inst.height);
                          const l = resolveEntryFt(inst.length);
                          if (!h || !l) return null;
                          const totalSF = parseFloat((qty * h * l).toFixed(2));
                          return (
                            <div className="flex items-center gap-2 px-2 py-1.5 bg-blue-950/30 border border-blue-800/40 rounded-lg">
                              <span className="text-[9px] text-blue-400 uppercase tracking-wide">
                                Coverage
                              </span>
                              <span className="text-[10px] font-mono text-blue-300">
                                {qty} × {h.toFixed(3)} × {l.toFixed(3)} ={" "}
                                {totalSF} SF
                              </span>
                            </div>
                          );
                        }
                        return null;
                      })()}
                  </div>
                )}

                {/* Per-instance summary bar — shown as soon as qty > 0 */}
                <SummaryBar qty={instQty} />
              </div>
            );
          })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ManualLaborRow — read-only compat for EditableRequestCard
// ─────────────────────────────────────────────────────────────────────────────
function ManualLaborRow({
  item,
  idx,
  onChange,
  onRemove,
  hstRate,
  workerBurdenMultiplier,
  readOnly,
}) {
  const sf = isSF(item.uom);
  const lf = isLF(item.uom);
  const needsDims = sf || lf;
  const laborQty =
    readOnly && item.computedCoverage != null && item.computedCoverage > 0
      ? item.computedCoverage
      : calcManualQty(item);
  const workerRate =
    item.uom === "HR" || workerBurdenMultiplier <= 1
      ? item.unitRate || 0
      : parseFloat(((item.unitRate || 0) / workerBurdenMultiplier).toFixed(2));
  const workerEarnings = parseFloat((laborQty * workerRate).toFixed(2));

  return (
    <div
      className={cls(
        "rounded-xl border bg-gray-800 transition-colors",
        laborQty > 0 ? "border-orange-500/40" : "border-gray-700",
      )}
    >
      <div className="flex items-start justify-between gap-3 px-4 pt-4 pb-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-200 truncate">
            {item.description}
          </p>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className="text-[10px] font-mono text-orange-400">
              {item.itemCode}
            </span>
            <span className="text-[10px] text-gray-600">·</span>
            <span className="text-[10px] text-gray-500">{item.uom}</span>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="text-right">
            <p className="text-[10px] text-gray-500">
              {workerBurdenMultiplier > 1 ? "Your pay rate" : "Unit rate"}
            </p>
            <p className="text-sm font-mono font-bold text-green-400">
              ${fmtRate(workerRate)}/{item.uom}
            </p>
          </div>
          {!readOnly && onRemove && (
            <button
              onClick={() => onRemove(idx)}
              className="text-gray-600 hover:text-red-400 transition-colors p-1 rounded shrink-0"
            >
              <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          )}
        </div>
      </div>
      <div className="px-4 pb-4 space-y-2">
        {readOnly ? (
          <div className="flex items-center gap-4 text-xs text-gray-400 flex-wrap">
            {needsDims ? (
              <>
                {sf && item.height > 0 && (
                  <span>H: {fmtQty(item.height)} ft</span>
                )}
                {item.length > 0 && (
                  <span>
                    {sf ? "W" : "L"}: {fmtQty(item.length)} ft
                  </span>
                )}
                {laborQty > 0 && (
                  <span className="text-blue-300 font-mono font-semibold">
                    = {fmtQty(laborQty)} {lf ? "LF" : "SF"}
                  </span>
                )}
              </>
            ) : laborQty > 0 ? (
              <span>Qty: {fmtQty(laborQty)}</span>
            ) : null}
          </div>
        ) : needsDims ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {sf && (
              <div>
                <label className="text-[9px] text-gray-500 uppercase tracking-wide block mb-1">
                  Height (ft)
                </label>
                <NumInput
                  value={item.height || ""}
                  onChange={(v) => onChange(idx, "height", v)}
                  placeholder="0"
                />
              </div>
            )}
            <div>
              <label className="text-[9px] text-gray-500 uppercase tracking-wide block mb-1">
                {sf ? "Width (ft)" : "Length (ft)"}
              </label>
              <NumInput
                value={item.length || ""}
                onChange={(v) => onChange(idx, "length", v)}
                placeholder="0"
              />
            </div>
            {laborQty > 0 && (
              <div>
                <label className="text-[9px] text-gray-500 uppercase tracking-wide block mb-1">
                  Total {lf ? "LF" : "SF"}
                </label>
                <div className="px-2 py-1.5 text-xs bg-gray-900 border border-gray-700 rounded-lg text-blue-300 font-mono font-semibold h-[30px] flex items-center">
                  {fmtQty(laborQty)}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div>
            <label className="text-[9px] text-gray-500 uppercase tracking-wide block mb-1">
              Qty
            </label>
            <NumInput
              value={item.quantity || ""}
              onChange={(v) => onChange(idx, "quantity", v)}
              placeholder="0"
              step="1"
            />
          </div>
        )}
        {/* Summary bar — edit and read-only */}
        {laborQty > 0 &&
          (() => {
            const sub = parseFloat((laborQty * workerRate).toFixed(2));
            return (
              <div className="grid grid-cols-3 divide-x divide-gray-700/50 bg-gray-900/60 rounded-lg border border-gray-700 overflow-hidden mt-2">
                {[
                  {
                    label: "Qty",
                    value: (
                      <span className="text-blue-300 font-bold font-mono text-xs">
                        {fmtQty(laborQty)}{" "}
                        <span className="text-gray-500 text-[9px] font-normal">
                          {item.uom}
                        </span>
                      </span>
                    ),
                  },
                  {
                    label: workerBurdenMultiplier > 1 ? "Your rate" : "Rate",
                    value: (
                      <span className="text-gray-300 font-mono text-xs">
                        ${fmtRate(workerRate)}
                      </span>
                    ),
                  },
                  {
                    label: "Subtotal",
                    value: (
                      <span className="text-orange-300 font-bold font-mono text-xs">
                        ${fmt(sub)}
                      </span>
                    ),
                  },
                ].map(({ label, value }) => (
                  <div key={label} className="px-2 py-2 flex flex-col gap-0.5">
                    <span className="text-[8px] text-gray-500 uppercase tracking-wide">
                      {label}
                    </span>
                    {value}
                  </div>
                ))}
              </div>
            );
          })()}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LaborTotalsFooter
// ─────────────────────────────────────────────────────────────────────────────
// Replace LaborTotalsFooter:
// ─────────────────────────────────────────────────────────────────────────────
// LaborTotalsFooter
// ─────────────────────────────────────────────────────────────────────────────
export function LaborTotalsFooter({
  laborSubtotal,
  hrSubtotal = 0,
  hstRate,
  workerBurdenMultiplier,
}) {
  if (laborSubtotal <= 0 && hrSubtotal <= 0) return null;

  // laborSubtotal is already built from paired.labor.unitRate, which is the
  // WORKER's pay rate (same rate ModulePairedRow's SummaryBar uses for each
  // row's subtotal — no burden division happens there). Dividing by
  // workerBurdenMultiplier again here double-applies the burden deduction
  // and makes the footer total lower than the sum of the row subtotals
  // shown above it. Just add the pieces together.
  const combined = parseFloat((laborSubtotal + hrSubtotal).toFixed(2));

  // Only compute HST if hstRate is a positive number — callers pass 0 for
  // workers without collectsTax, matching the effectiveHstRate pattern in
  // piecework-request-create-v3.js
  const showHst = (hstRate || 0) > 0;
  const hst = showHst ? parseFloat(((combined * hstRate) / 100).toFixed(2)) : 0;
  const total = combined + hst;

  return (
    <div className="bg-gray-900 rounded-xl border border-gray-700 px-4 py-3">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex gap-5 flex-wrap">
          <div>
            <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-0.5">
              Subtotal
            </p>
            <p className="text-sm font-mono text-gray-400">${fmt(combined)}</p>
          </div>
          {showHst && (
            <div>
              <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-0.5">
                HST ({hstRate}%)
              </p>
              <p className="text-sm font-mono text-gray-300">${fmt(hst)}</p>
            </div>
          )}
        </div>
        <div className="text-right">
          <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-0.5">
            Total
          </p>
          <p className="text-xl font-bold font-mono text-orange-400">
            ${fmt(total)}
          </p>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main export
// ─────────────────────────────────────────────────────────────────────────────
export default function SharedPieceworkItemEntry({
  module,
  // New instance-based API
  instances = [],
  onInstChange,
  onDuplicate,
  onRemove,
  sizeErrors = new Set(),
  // Work completed date
  workDate,
  onWorkDateChange,
  // Legacy compat (read-only)
  entries = {},
  onEntryChange,
  manualItems = [],

  onManualChange,
  onManualRemove,
  hstRate = 13,
  workerBurdenMultiplier = 1,
  hideZeroRows = false,
  totalBurdenPct = 0,
  readOnly = false,
  savedCoverages = {},
  savedPieceCounts = {},
}) {
  const today = new Date().toISOString().slice(0, 10);

  const byPaired = useMemo(() => {
    const map = new Map();
    for (const inst of instances) {
      if (!map.has(inst.pairedIdx)) map.set(inst.pairedIdx, []);
      map.get(inst.pairedIdx).push(inst);
    }
    return map;
  }, [instances]);

  // Replace the laborSubtotal useMemo:
  const { laborSubtotal, hrSubtotal } = useMemo(() => {
    let sub = 0;
    let hr = 0;
    if (module) {
      (module.pairedItems || []).forEach((p, pIdx) => {
        if (readOnly && savedCoverages[pIdx] != null) {
          sub += savedCoverages[pIdx] * (p.labor?.unitRate || 0);
        } else {
          (byPaired.get(pIdx) || []).forEach((inst) => {
            sub += calcLaborQtyInst(p, inst) * (p.labor?.unitRate || 0);
          });
        }
      });
    }
    manualItems.forEach((item) => {
      const qty =
        readOnly && item.computedCoverage != null && item.computedCoverage > 0
          ? item.computedCoverage
          : calcManualQty(item);
      const amount = qty * (item.unitRate || 0);
      if (item.uom === "HR") {
        hr += amount; // HR: no burden deduction
      } else {
        sub += amount; // piecework: will be divided by multiplier
      }
    });
    return { laborSubtotal: sub, hrSubtotal: parseFloat(hr.toFixed(2)) };
  }, [module, instances, byPaired, manualItems, readOnly, savedCoverages]);
  const activeCount = useMemo(() => {
    let n = 0;
    if (module) {
      (module.pairedItems || []).forEach((p, pIdx) => {
        const total =
          readOnly && savedCoverages[pIdx] != null
            ? savedCoverages[pIdx]
            : (byPaired.get(pIdx) || []).reduce(
                (s, inst) => s + calcLaborQtyInst(p, inst),
                0,
              );
        if (total > 0) n++;
      });
    }
    return n;
  }, [module, instances, byPaired, readOnly, savedCoverages]);

  return (
    <div className="space-y-3">
      {/* Work completed date — shown in items step */}
      {!readOnly && onWorkDateChange && (
        <div className="flex items-center gap-3 px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-xl">
          <div className="flex-1">
            <label className="text-[10px] text-gray-500 uppercase tracking-wide block mb-1">
              Work Completed Date
            </label>
            <input
              type="date"
              value={workDate || today}
              max={today}
              onChange={(e) => onWorkDateChange(e.target.value)}
              className="w-full px-2 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-200 focus:border-orange-500 focus:outline-none"
            />
          </div>
          {workDate && workDate !== today && (
            <button
              onClick={() => onWorkDateChange(today)}
              className="text-[10px] text-gray-500 hover:text-gray-300 transition-colors shrink-0 mt-4"
            >
              Reset to today
            </button>
          )}
        </div>
      )}

      {module && (
        <div className="flex items-center gap-2 px-1">
          <span className="text-[10px] text-gray-500">Template:</span>
          <span className="text-[10px] font-medium text-orange-400">
            {module.name}
          </span>
          {activeCount > 0 && (
            <span className="text-[10px] bg-orange-900/40 text-orange-300 border border-orange-700/40 px-1.5 py-0.5 rounded-full">
              {activeCount} active
            </span>
          )}
        </div>
      )}

      {module &&
        (module.pairedItems || []).map((paired, pIdx) => {
          const insts = readOnly
            ? [
                {
                  _instId: `ro_${pIdx}`,
                  pairedIdx: pIdx,
                  ...(entries[pIdx] || {}),
                },
              ]
            : byPaired.get(pIdx) || [];

          if (readOnly && hideZeroRows) {
            const cov = savedCoverages[pIdx];
            if (!cov || cov <= 0) return null;
          }

          return (
            <ModulePairedRow
              key={pIdx}
              paired={paired}
              pairedIdx={pIdx}
              instances={insts}
              onInstChange={readOnly ? () => {} : onInstChange}
              onDuplicate={readOnly ? () => {} : onDuplicate}
              onRemove={readOnly ? () => {} : onRemove}
              workerBurdenMultiplier={workerBurdenMultiplier}
              hstRate={hstRate}
              readOnly={readOnly}
              savedCoverage={readOnly ? (savedCoverages[pIdx] ?? null) : null}
              savedPieceCount={
                readOnly ? (savedPieceCounts[pIdx] ?? null) : null
              }
              sizeErrors={sizeErrors}
            />
          );
        })}

      {manualItems.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-white px-1">
            {module ? "Additional Items" : "Labor Items"}
          </h4>
          {manualItems.map((item, idx) => (
            <ManualLaborRow
              key={item._id || idx}
              item={item}
              idx={idx}
              onChange={readOnly ? () => {} : onManualChange}
              onRemove={readOnly ? null : onManualRemove}
              hstRate={hstRate}
              workerBurdenMultiplier={workerBurdenMultiplier}
              readOnly={readOnly}
            />
          ))}
        </div>
      )}

      {!module && manualItems.length === 0 && !readOnly && (
        <div className="py-8 text-center border border-dashed border-gray-700 rounded-xl">
          <p className="text-xs text-gray-500">No items yet.</p>
        </div>
      )}

      <LaborTotalsFooter
        laborSubtotal={laborSubtotal}
        hrSubtotal={hrSubtotal}
        hstRate={hstRate}
        workerBurdenMultiplier={workerBurdenMultiplier}
      />
    </div>
  );
}
